import { useEffect, useRef } from "react";
import { motion } from "framer-motion";

const IMAGE_WIDTH = 1672;
const IMAGE_HEIGHT = 941;
const PORTRAIT_PATH = "M671 0C815-6 1010 1 1120 78c65 52 70 135 64 192 27 42 36 90 33 140-3 65-35 136-51 174-16 62-5 122 25 182l79 175H355c95-93 186-185 281-269l75-21c13-43 19-99 4-146-45-26-82-75-92-134-18-69-14-166-1-239C633 77 647 31 671 0Z";

const random = (seed) => {
  const value = Math.sin(seed * 127.1) * 43758.5453;
  return value - Math.floor(value);
};

const makeParticles = (image, width, height, path) => {
  const sample = document.createElement("canvas");
  sample.width = Math.round(width);
  sample.height = Math.round(height);
  const context = sample.getContext("2d", { willReadFrequently: true });
  if (!context) return [];
  context.drawImage(image, 0, 0, sample.width, sample.height);
  const pixels = context.getImageData(0, 0, sample.width, sample.height).data;
  const step = Math.max(6, Math.round(width / 115));
  const particles = [];

  for (let y = 0, row = 0; y < height; y += step, row += 1) {
    for (let x = 0, column = 0; x < width; x += step, column += 1) {
      if (!context.isPointInPath(path, ((x + step / 2) / width) * IMAGE_WIDTH, ((y + step / 2) / height) * IMAGE_HEIGHT)) continue;
      let bestIndex = -1;
      let bestScore = 5000;
      let bestX = x;
      let bestY = y;
      for (let pixelY = Math.floor(y); pixelY < Math.min(sample.height, y + step); pixelY += 1) {
        for (let pixelX = Math.floor(x); pixelX < Math.min(sample.width, x + step); pixelX += 1) {
          const index = (pixelY * sample.width + pixelX) * 4;
          const alpha = pixels[index + 3];
          const brightness = (pixels[index] + pixels[index + 1] + pixels[index + 2]) / 3;
          const score = alpha * (255 - brightness);
          if (score > bestScore) {
            bestIndex = index;
            bestScore = score;
            bestX = pixelX;
            bestY = pixelY;
          }
        }
      }
      if (bestIndex < 0) continue;

      const seed = row * 131 + column * 17 + 1;
      particles.push({
        x: bestX,
        y: bestY,
        radius: step * (0.24 + random(seed) * 0.15),
        spread: 25 + random(seed + 1) * 48,
        angle: random(seed + 2) * Math.PI * 2,
        color: "rgba(" + pixels[bestIndex] + "," + pixels[bestIndex + 1] + "," + pixels[bestIndex + 2] + "," + pixels[bestIndex + 3] / 255 + ")",
      });
    }
  }

  return particles;
};

const PortraitScatter = ({ src, alt, reduceMotion, y }) => {
  const shellRef = useRef(null);
  const imageRef = useRef(null);
  const canvasRef = useRef(null);
  const hoverRef = useRef(null);

  useEffect(() => {
    if (reduceMotion || !window.matchMedia("(hover: hover) and (pointer: fine)").matches) return undefined;

    const shell = shellRef.current;
    const stillImage = imageRef.current;
    const canvas = canvasRef.current;
    const hover = hoverRef.current;
    const context = canvas.getContext("2d");
    if (!context) return undefined;

    const source = new Image();
    source.crossOrigin = "anonymous";
    const portraitPath = new Path2D(PORTRAIT_PATH);
    let particles = [];
    let width = 0;
    let height = 0;
    let radius = 0;
    let ratio = 1;
    let pointer = null;
    let progress = 0;
    let target = 0;
    let frameId = 0;
    let disposed = false;

    const draw = () => {
      if (!width || !height || !source.naturalWidth) return;
      context.clearRect(0, 0, width, height);
      context.drawImage(source, 0, 0, width, height);
      if (!pointer || progress < 0.003) return;

      context.save();
      context.scale(width / IMAGE_WIDTH, height / IMAGE_HEIGHT);
      context.clip(portraitPath);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.globalCompositeOperation = "destination-out";
      const hole = context.createRadialGradient(pointer.x, pointer.y, 0, pointer.x, pointer.y, radius);
      hole.addColorStop(0, "rgba(0,0,0," + progress + ")");
      hole.addColorStop(0.55, "rgba(0,0,0," + progress * 0.92 + ")");
      hole.addColorStop(1, "rgba(0,0,0,0)");
      context.fillStyle = hole;
      context.beginPath();
      context.arc(pointer.x, pointer.y, radius, 0, Math.PI * 2);
      context.fill();
      context.restore();

      for (const particle of particles) {
        const dx = particle.x - pointer.x;
        const dy = particle.y - pointer.y;
        const distance = Math.hypot(dx, dy);
        if (distance > radius) continue;
        const influence = 1 - distance / radius;
        const angle = distance > 2 ? Math.atan2(dy, dx) : particle.angle;
        const spread = particle.spread * progress * (0.35 + influence * 0.65);
        context.globalAlpha = progress * (0.65 + influence * 0.35);
        context.fillStyle = particle.color;
        context.beginPath();
        context.arc(
          particle.x + Math.cos(angle) * spread,
          particle.y + Math.sin(angle) * spread,
          particle.radius,
          0,
          Math.PI * 2,
        );
        context.fill();
      }
      context.globalAlpha = 1;
    };

    const animate = () => {
      progress += (target - progress) * 0.16;
      if (Math.abs(target - progress) < 0.004) progress = target;
      draw();
      if (progress === 0) {
        stillImage.style.opacity = "1";
        canvas.style.opacity = "0";
      }
      frameId = progress === target ? 0 : window.requestAnimationFrame(animate);
    };

    const setTarget = (value) => {
      if (!particles.length) return;
      target = value;
      if (value === 1) {
        draw();
        canvas.style.opacity = "1";
        stillImage.style.opacity = "0";
      }
      if (!frameId) frameId = window.requestAnimationFrame(animate);
    };

    const resize = () => {
      if (!source.complete || !source.naturalWidth || disposed) return;
      const bounds = shell.getBoundingClientRect();
      width = bounds.width;
      height = bounds.height;
      if (!width || !height) return;
      ratio = Math.min(window.devicePixelRatio || 1, 2);
      radius = Math.max(42, Math.min(68, width * 0.09));
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      particles = makeParticles(source, width, height, portraitPath);
      draw();
    };

    const updatePointer = (event) => {
      const bounds = shell.getBoundingClientRect();
      pointer = { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
      if (target === 1) draw();
    };
    const enter = (event) => {
      if (event.pointerType !== "mouse" && event.pointerType !== "pen") return;
      updatePointer(event);
      setTarget(1);
    };
    const leave = () => setTarget(0);
    const observer = new ResizeObserver(resize);

    hover.addEventListener("pointerenter", enter);
    hover.addEventListener("pointermove", updatePointer);
    hover.addEventListener("pointerleave", leave);
    window.addEventListener("blur", leave);
    observer.observe(shell);
    source.onload = resize;
    source.src = src;
    if (source.complete) resize();

    return () => {
      disposed = true;
      window.cancelAnimationFrame(frameId);
      observer.disconnect();
      hover.removeEventListener("pointerenter", enter);
      hover.removeEventListener("pointermove", updatePointer);
      hover.removeEventListener("pointerleave", leave);
      window.removeEventListener("blur", leave);
      source.onload = null;
      stillImage.style.opacity = "1";
      canvas.style.opacity = "0";
    };
  }, [src, reduceMotion]);

  return (
    <motion.div
      ref={shellRef}
      style={reduceMotion ? undefined : { y }}
      data-testid="hero-portrait-scatter"
      className="hero-portrait relative z-10 aspect-[1672/941] w-[540px] max-w-none select-none sm:w-[680px] lg:h-full lg:w-auto"
    >
      <img
        ref={imageRef}
        src={src}
        alt={alt}
        width={IMAGE_WIDTH}
        height={IMAGE_HEIGHT}
        fetchPriority="high"
        decoding="async"
        className="pointer-events-none absolute inset-0 size-full"
      />
      <canvas ref={canvasRef} aria-hidden="true" className="pointer-events-none absolute inset-0 size-full opacity-0" />
      <div ref={hoverRef} aria-hidden="true" className="hero-portrait-hover absolute" />
    </motion.div>
  );
};

export default PortraitScatter;
